
import { useTranslations } from "use-intl";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Play, AlertTriangle } from "lucide-react";
import type { Workflow } from "@/types/workflow.types";

interface RunConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
  workflow: Workflow | null;
  disabledReason?: string | null;
}

export default function RunConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  workflow,
  disabledReason,
}: RunConfirmModalProps) {
  const t = useTranslations("workflows");
  if (!workflow) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" aria-label={t("modals.run_title")}>
      <ModalHeader>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t("modals.run_title")}
        </h3>
      </ModalHeader>
      <ModalBody className="space-y-4">
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/50 dark:bg-amber-900/20">
          <AlertTriangle className="mt-0.5 size-5 shrink-0 text-amber-500" />
          <div className="text-sm text-amber-700 dark:text-amber-400">
            <p>
              {t("modals.run_warning", { name: "" })}
              <strong>{workflow.name}</strong>.
            </p>
            {workflow.scheduleType === "cron" && workflow.cronExpression && (
              <p className="mt-1">
                {t("modals.run_schedule_label")} <code className="rounded bg-amber-100 px-1 py-0.5 text-xs dark:bg-amber-900/30">{workflow.cronExpression}</code>
              </p>
            )}
          </div>
        </div>
        {disabledReason && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
            {disabledReason}
          </div>
        )}
      </ModalBody>
      <ModalFooter className="flex justify-end gap-3">
        <Button variant="outline" onClick={onClose} disabled={isLoading}>
          {t("modals.run_cancel")}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={isLoading || !!disabledReason}
          startIcon={<Play className="size-4" />}
        >
          {isLoading ? t("modals.run_starting") : t("modals.run_now")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
